import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { appsAPI } from '../api/appsAPI';
import UserProfileDropdown from '../../../components/UserProfileDropdown/UserProfileDropdown';
import { Logo_black_iconOnly } from '../../../components/Logo/Logo';
import { getTutorialIconLabel } from '../utils/appDisplayUtils';
import './AppStoreView.css';
import './LandingAppStore.css';

const promoEvents = [
  {
    key: 'uai',
    badge: 'Ends on 07/31',
    headline: 'Plan your studies, organize tasks, and get instant feedback with uAI',
    description: 'Unleash your potential with our advanced AI tutor, personalized study planner, and instant case feedback.',
    appTitle: 'uAI Study Assistant',
    appSubtext: 'Ivori • AI Tools',
    defaultIcon: '/uai-icon.png',
    defaultBanner: '/api/apps/media/tutorials/uai/uai-1920x1080.png'
  },
  {
    key: 'code_lab',
    badge: 'Ends on 07/15',
    headline: 'Learn dynamic coding and run code in real-time',
    description: 'Practice Python, JS and SQL directly in your browser with interactive sandboxed code playgrounds.',
    appTitle: 'Code Lab',
    appSubtext: 'Ivori • Coding',
    defaultIcon: '/logo192.png',
    defaultBanner: '/api/apps/media/tutorials/code_lab/code_lab-1920x1080.png'
  },
  {
    key: 'computer_science_interview',
    badge: 'Ends on 06/30',
    headline: 'Prep for top tech companies with mock programming cases',
    description: 'Crack the algorithmic and design interviews using our step-by-step case interactive simulator.',
    appTitle: 'Computer Science Interview',
    appSubtext: 'Ivori • Computer Science',
    defaultIcon: '/logo192.png',
    defaultBanner: '/api/apps/media/tutorials/computer_science_interview/computer_science_interview-1920x1080.png'
  }
];

const APPS_PER_PAGE = 8;

const dedupeByExamOrTitle = (list) =>
  list.reduce((acc, app) => {
    const key = app.exam_name || app.title;
    if (!acc.find((e) => (e.exam_name || e.title) === key)) acc.push(app);
    return acc;
  }, []);

const sortSubjectsPreferredFirst = (subjectKeys, preferredSubjects) => {
  const pref = preferredSubjects.map((s) => (s || '').toLowerCase()).filter(Boolean);
  const rank = (subj) => {
    const sl = (subj || '').toLowerCase();
    let best = Infinity;
    pref.forEach((p, i) => {
      if (sl === p || sl.includes(p) || p.includes(sl)) best = Math.min(best, i);
    });
    return best;
  };
  return [...subjectKeys].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    return String(a).localeCompare(String(b));
  });
};

function PaginatedSubjectRow({ title, variant, apps, onAppClick, formatRatingCount }) {
  const [slideIndex, setSlideIndex] = useState(0);
  const itemsPerPage = APPS_PER_PAGE;
  const sliderCount = Math.max(1, Math.ceil(apps.length / itemsPerPage));
  const slideIdx = Math.min(slideIndex, sliderCount - 1);
  const slice = apps.slice(slideIdx * itemsPerPage, slideIdx * itemsPerPage + itemsPerPage);

  useEffect(() => {
    setSlideIndex(0);
  }, [title, apps.length]);

  const boxClass = 'trending-box case-apps-row-box';
  const rowClass = 'case-apps-row';

  return (
    <div className={`column-box ${boxClass}`}>
      <h2 className="column-box-title">{title}</h2>
      <div className="case-apps-row-wrapper" style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
        <button
          type="button"
          className="case-scroll-btn case-scroll-btn-left"
          onClick={() => setSlideIndex(prev => Math.max(0, prev - 1))}
          disabled={slideIdx === 0}
          aria-label="Previous page"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className={rowClass}>
          {slice.map((app) => {
            const rating = app.rating !== undefined ? app.rating : 4.5;
            const imageUrl = app.images?.medium || app.images?.large || app.images?.small || '/logo192.png';
            return (
              <div
                key={app.id}
                role="button"
                tabIndex={0}
                className="column-box-item"
                onClick={() => onAppClick(app.id)}
                onKeyDown={(e) => e.key === 'Enter' && onAppClick(app.id)}
              >
                <img src={imageUrl} alt={app.title} className="column-box-icon" />
                <div className="column-box-info">
                  <h3 className="column-box-item-title">{app.title}</h3>
                </div>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className="case-scroll-btn case-scroll-btn-right"
          onClick={() => setSlideIndex(prev => Math.min(sliderCount - 1, prev + 1))}
          disabled={slideIdx === sliderCount - 1}
          aria-label="Next page"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      {sliderCount > 1 && (
        <div className="case-slider-dots">
          {Array.from({ length: sliderCount }, (_, index) => (
            <button
              key={index}
              type="button"
              className={`case-slider-dot ${index === slideIdx ? 'case-slider-dot-active' : ''}`}
              onClick={() => setSlideIndex(index)}
              aria-label={`Page ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const MarketplaceView = () => {
  const [apps, setApps] = useState([]);
  const [forYouSubjects, setForYouSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [productivitySlideIndex, setProductivitySlideIndex] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  const eventsRowRef = useRef(null);
  const [canScrollEventsLeft, setCanScrollEventsLeft] = useState(false);
  const [canScrollEventsRight, setCanScrollEventsRight] = useState(true);

  const updateEventsScrollButtons = () => {
    const el = eventsRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollEventsLeft(scrollLeft > 10);
      setCanScrollEventsRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const handleEventsScrollClick = (direction) => {
    const el = eventsRowRef.current;
    if (el) {
      const cardWidth = 384; // 360px width + 24px gap
      el.scrollBy({
        left: direction === 'left' ? -cardWidth : cardWidth,
        behavior: 'smooth',
      });
    }
  };

  const userName = user?.name || 'Student';
  const userEmail = user?.email || 'student@pathbuddy.com';

  useEffect(() => {
    const fetchApps = async () => {
      try {
        setLoading(true);
        const res = await appsAPI.getAppsForUser().catch(() => appsAPI.getApps());
        const list = Array.isArray(res) ? res : (res?.apps || res || []);
        const subjects = Array.isArray(res?.for_you_subjects) ? res.for_you_subjects : [];
        setApps(list);
        setForYouSubjects(subjects);
      } catch (error) {
        console.error('Error fetching apps for marketplace:', error);
        setApps([]);
        setForYouSubjects([]);
      } finally {
        setLoading(false);
      }
    };
    fetchApps();
  }, []);

  useEffect(() => {
    const timer = setTimeout(updateEventsScrollButtons, 200);
    return () => clearTimeout(timer);
  }, [apps]);

  useEffect(() => {
    window.addEventListener('resize', updateEventsScrollButtons);
    return () => window.removeEventListener('resize', updateEventsScrollButtons);
  }, []);

  useEffect(() => {
    setProductivitySlideIndex(0);
  }, [searchQuery, selectedCategory, apps.length]);

  // Filter apps based on both category and search query
  const filteredApps = apps.filter((app) => {
    // 1. Filter by Search Query
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch = !q || (
      (app.title || '').toLowerCase().includes(q) ||
      (app.subject || '').toLowerCase().includes(q) ||
      (app.creator || '').toLowerCase().includes(q) ||
      (app.description || '').toLowerCase().includes(q) ||
      (app.exam_name || '').toLowerCase().includes(q)
    );

    if (!matchesSearch) return false;

    // 2. Filter by Category
    if (selectedCategory === 'All') return true;
    if (selectedCategory === 'Productivity & Utility') {
      return app.app_type === 'productivity' || app.app_type === 'utility';
    }
    return (app.subject || '').toLowerCase() === selectedCategory.toLowerCase();
  });

  // Decide if search/filter is active
  const isFilteringActive = searchQuery.trim() !== '' || selectedCategory !== 'All';

  const experienceApps = filteredApps.filter((app) => app.app_type === 'case');
  const tutorialApps = filteredApps.filter((app) => app.app_type === 'tutorial');
  const productivityAndUtilityApps = filteredApps.filter((app) => app.app_type === 'productivity' || app.app_type === 'utility');
  const uniqueProductivityUtility = dedupeByExamOrTitle(productivityAndUtilityApps);

  const appsBySubject = experienceApps.reduce((acc, app) => {
    const subject = app.subject || 'Other';
    if (!acc[subject]) acc[subject] = [];
    acc[subject].push(app);
    return acc;
  }, {});

  const tutorialsBySubject = dedupeByExamOrTitle(tutorialApps).reduce((acc, app) => {
    const subject = app.subject || 'Other';
    if (!acc[subject]) acc[subject] = [];
    acc[subject].push(app);
    return acc;
  }, {});

  const allLayerSubjects = new Set([...Object.keys(appsBySubject), ...Object.keys(tutorialsBySubject)]);
  const mergedSubjectOrder = sortSubjectsPreferredFirst([...allLayerSubjects], forYouSubjects);

  // Categories list for pills (derived dynamically from the loaded apps)
  const uniqueSubjectsList = Array.from(new Set(apps.map((app) => app.subject).filter(Boolean)));
  const sortedSubjectsForPills = sortSubjectsPreferredFirst(uniqueSubjectsList, forYouSubjects);
  const categoriesList = ['All', ...sortedSubjectsForPills, 'Productivity & Utility'];

  const handleAppClick = (appId) => {
    const app = apps.find(a => a.id === appId);
    const isUai = app && ((app.exam_name || '').trim().toLowerCase() === 'uai' ||
      ((app.title || '').trim().toLowerCase() === 'uai'));
    if (isUai) {
      navigate('/chat');
      return;
    }
    navigate(`/apps-store/${appId}`);
  };

  const formatRatingCount = (count) => {
    if (!count || count === 0) return '0';
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
    return count.toString();
  };

  const productivityItemsPerPage = 8;
  const productivitySliderCount = Math.max(1, Math.ceil(uniqueProductivityUtility.length / productivityItemsPerPage));
  const productivitySlideIdx = Math.min(productivitySlideIndex, productivitySliderCount - 1);
  const productivityAppsToRender = uniqueProductivityUtility.slice(productivitySlideIdx * productivityItemsPerPage, productivitySlideIdx * productivityItemsPerPage + productivityItemsPerPage);

  if (loading) {
    return (
      <div className="app-store-view netflix-style">
        <div className="app-store-loading">
          <div className="loading-spinner" />
          <p>Loading Explore...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-store-view netflix-style">
      <header className="app-store-header">
        <div className="app-store-header-content">
          <div className="app-store-left-section">
            <div className="app-store-logo">
              <Logo_black_iconOnly onClick={() => navigate('/apps-store')} />
            </div>
            <h1 className="app-store-title">Marketplace</h1>
          </div>
          <div className="app-store-header-actions">
            <UserProfileDropdown userName={userName} userEmail={userEmail} />
          </div>
        </div>
      </header>

      <main className="app-store-main marketplace-inside" style={{ paddingBottom: '40px' }}>
        <div className="landing-app-store marketplace-landing-style">
          <div className="app-store-content app-store-content-fullwidth">
            
            {/* Search and Category Filter Section */}
            <div className="marketplace-controls-bar">
              <div className="marketplace-search-box">
                <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, subject, or creator..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="marketplace-search-input"
                />
                {searchQuery && (
                  <button 
                    type="button" 
                    className="clear-search-button" 
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="marketplace-category-pills">
                {categoriesList.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className={`category-pill ${selectedCategory === cat ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            {isFilteringActive && (
              <div className="marketplace-results-header" style={{ maxWidth: '1200px', margin: '20px auto 0', padding: '0 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--iv-line)', paddingBottom: '12px' }}>
                <h2 className="results-title" style={{ fontSize: '18px', color: 'var(--iv-muted)', fontWeight: 500, margin: 0 }}>
                  {searchQuery.trim() 
                    ? `Showing results for "${searchQuery}"` 
                    : `Showing ${selectedCategory} apps`}
                  <span className="results-count"> ({filteredApps.length} found)</span>
                </h2>
                <button 
                  type="button" 
                  className="reset-filters-btn" 
                  onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
                  style={{ background: 'none', border: 'none', color: 'var(--iv-accent)', fontWeight: 600, cursor: 'pointer' }}
                >
                  Clear Filters
                </button>
              </div>
            )}

            {filteredApps.length > 0 ? (
              <section className="app-store-section two-column-section marketplace-explore-section">
                <div className="two-column-container">
                  {mergedSubjectOrder.map((subject) => {
                    const caseList = appsBySubject[subject];
                    const tutList = tutorialsBySubject[subject];
                    return (
                      <Fragment key={subject}>
                        {caseList && caseList.length > 0 && (
                          <PaginatedSubjectRow
                            title={`${subject} · Case apps`}
                            variant="case"
                            apps={caseList}
                            onAppClick={handleAppClick}
                            formatRatingCount={formatRatingCount}
                          />
                        )}
                        {tutList && tutList.length > 0 && (
                          <PaginatedSubjectRow
                            title={`${subject} · Tutorial apps`}
                            variant="tutorial"
                            apps={tutList}
                            onAppClick={handleAppClick}
                            formatRatingCount={formatRatingCount}
                          />
                        )}
                      </Fragment>
                    );
                  })}

                  {/* Google Play Store Style Promotional App Banner */}
                  <div className="play-promo-banner-card">
                    <div className="play-promo-banner-left">
                      <h3 className="play-promo-banner-headline">
                        Plan your studies, organize tasks, and get instant feedback with uAI...
                      </h3>
                      <div className="play-promo-banner-app-row">
                        <img src="/uai-icon.png" alt="uAI Icon" className="play-promo-app-icon" />
                        <div className="play-promo-app-details">
                          <div className="play-promo-app-title">uAI Study Assistant</div>
                          {/* <div className="play-promo-app-subtext">Pathbuddy • AI Tools</div> */}
                        </div>
                        <div className="play-promo-action-container">
                          <button 
                            type="button" 
                            className="play-promo-install-btn"
                            onClick={() => navigate('/apps-store', { state: { openChat: true } })}
                          >
                            Try out now
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="play-promo-banner-right">
                      <img src="/uai_promo_banner.png" alt="uAI Promo Banner" className="play-promo-cover-img" />
                    </div>
                  </div>

                  {uniqueProductivityUtility && uniqueProductivityUtility.length > 0 && (
                    <div className="column-box trending-box case-apps-row-box">
                      <h2 className="column-box-title">Productivity and Utility apps</h2>
                      <div className="case-apps-row-wrapper" style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
                        <button
                          type="button"
                          className="case-scroll-btn case-scroll-btn-left"
                          onClick={() => setProductivitySlideIndex(prev => Math.max(0, prev - 1))}
                          disabled={productivitySlideIdx === 0}
                          aria-label="Previous page"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <div className="case-apps-row" id="marketplace-productivity-tools-row">
                          {productivityAppsToRender.map((app) => {
                            const rating = app.rating !== undefined ? app.rating : 4.5;
                            const imageUrl = app.images?.medium || app.images?.large || app.images?.small || '/uai-icon.png';
                            return (
                              <div
                                key={app.id}
                                role="button"
                                tabIndex={0}
                                className="column-box-item"
                                onClick={() => handleAppClick(app.id)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAppClick(app.id)}
                              >
                                <img src={imageUrl} alt={app.title} className="column-box-icon" />
                                <div className="column-box-info">
                                  <h3 className="column-box-item-title">{app.title}</h3>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="case-scroll-btn case-scroll-btn-right"
                          onClick={() => setProductivitySlideIndex(prev => Math.min(productivitySliderCount - 1, prev + 1))}
                          disabled={productivitySlideIdx === productivitySliderCount - 1}
                          aria-label="Next page"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                      {productivitySliderCount > 1 && (
                        <div className="case-slider-dots">
                          {Array.from({ length: productivitySliderCount }, (_, index) => (
                            <button
                              key={index}
                              type="button"
                              className={`case-slider-dot ${index === productivitySlideIdx ? 'case-slider-dot-active' : ''}`}
                              onClick={() => setProductivitySlideIndex(index)}
                              aria-label={`Page ${index + 1}`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>
            ) : (
              <div className="marketplace-no-results" style={{ maxWidth: '1200px', margin: '40px auto' }}>
                <div className="no-results-icon">🔍</div>
                <h3>No Apps Found</h3>
                <p>We couldn't find any apps matching your search criteria. Try using different keywords or resetting filters.</p>
                <button 
                  type="button" 
                  className="reset-button"
                  onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MarketplaceView;
